import type { useCreateResultsMutation } from 'prove-common'

export const createResultLine = () => ({
  name: '',
  efsaCode: '',
  amount: '',
  belowThreshold: false,
  detectableThreshold: '',
  unitOfMeasurement: '',
  method: '',
  suggestedInterpretation: ''
})

export type ResultLineDraft = ReturnType<typeof createResultLine>
export const createResult = () => ({
  id: crypto.randomUUID(),
  subSampleId: '',
  substance: '',
  suggestedInterpretation: '',
  resultLines: [] as ResultLineDraft[]
})
export const createResultsDraft = () => ({
  requisitionId: '',
  sampleId: '',
  results: [createResult()]
})
export type ResultsDraft = ReturnType<typeof createResultsDraft>
export type ToolErrors = Record<string, string>
type Mutation = ReturnType<typeof useCreateResultsMutation>
export type ResultsReceipt = NonNullable<
  Awaited<ReturnType<Mutation['mutateAsync']>>
>

// The shared mutation can resolve undefined for an empty HTTP error response.
// Only the contract's receipt confirms success, not promise resolution alone.
export const isResultsReceipt = (
  data: ResultsReceipt | undefined,
  sampleId: string
): data is ResultsReceipt =>
  !!data &&
  data.id === sampleId &&
  Array.isArray(data.results) &&
  data.results.every(
    (result) =>
      !!result &&
      Number.isSafeInteger(result.id) &&
      (result.subSampleId === null ||
        Number.isSafeInteger(result.subSampleId)) &&
      (result.substance === undefined ||
        result.substance === null ||
        typeof result.substance === 'string') &&
      Array.isArray(result.resultLines)
  ) &&
  Number.isSafeInteger(data.reportedSubstanceCount) &&
  Number.isSafeInteger(data.totalSubstanceCount) &&
  Array.isArray(data.pendingSubstanceCodes) &&
  data.pendingSubstanceCodes.every((code) => typeof code === 'string')

// Decimal syntax only: Number() alone also accepts hex and empty strings.
export const isDecimalNumber = (value: string) =>
  /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i.test(value.trim()) &&
  Number.isFinite(Number(value))

export const isIntegerId = (value: string) =>
  /^[+-]?\d+$/.test(value.trim()) && Number.isSafeInteger(Number(value))

const interpretation = (value: string) => {
  if (value === 'null') return null
  if (value === 'pos' || value === 'neg' || value === 'inc') return value
  return undefined
}

export const validateResults = (draft: ResultsDraft): ToolErrors => {
  const errors: ToolErrors = {}
  if (!isIntegerId(draft.requisitionId)) {
    errors.requisitionId = 'Rekvisisjons-ID må være et heltall.'
  }
  if (!draft.sampleId.trim()) errors.sampleId = 'Prøve-ID må fylles ut.'
  if (!draft.results.length)
    errors['add-result'] = 'Legg til minst ett resultat.'
  draft.results.forEach((result, index) => {
    const prefix = `result-${result.id}`
    if (result.subSampleId.trim() && !isIntegerId(result.subSampleId)) {
      errors[`${prefix}-subSampleId`] =
        `Resultat ${index + 1}: Delprøve-ID må være et heltall.`
    }
    result.resultLines.forEach((line, lineIndex) => {
      const label = `Resultat ${index + 1}, linje ${lineIndex + 1}`
      for (const [key, name] of [
        ['name', 'Navn'],
        ['method', 'Metode'],
        ['unitOfMeasurement', 'Måleenhet']
      ] as const) {
        if (!line[key].trim())
          errors[`${prefix}-${lineIndex}-${key}`] =
            `${label}: ${name} må fylles ut.`
      }
      for (const [key, name] of [
        ['amount', 'Mengde'],
        ['detectableThreshold', 'Deteksjonsgrense']
      ] as const) {
        if (line[key].trim() && !isDecimalNumber(line[key])) {
          errors[`${prefix}-${lineIndex}-${key}`] =
            `${label}: ${name} må være et tall. Bruk punktum som desimalskilletegn.`
        }
      }
    })
  })
  return errors
}

export const buildResultsRequest = (
  draft: ResultsDraft
): Parameters<Mutation['mutateAsync']>[0] => {
  if (Object.keys(validateResults(draft)).length)
    throw new Error('Rett feltene før innsending.')
  const optionalNumber = (value: string) =>
    value.trim() ? Number(value) : undefined
  return {
    requisitionId: Number(draft.requisitionId),
    body: {
      id: draft.sampleId.trim(),
      results: draft.results.map((result) => ({
        subSampleId: optionalNumber(result.subSampleId),
        substance: result.substance.trim() || undefined,
        suggestedInterpretation: interpretation(result.suggestedInterpretation),
        resultLines: result.resultLines.map((line) => ({
          name: line.name.trim(),
          method: line.method.trim(),
          unitOfMeasurement: line.unitOfMeasurement.trim(),
          efsaCode: line.efsaCode.trim() || undefined,
          amount: optionalNumber(line.amount),
          detectableThreshold: optionalNumber(line.detectableThreshold),
          belowThreshold: line.belowThreshold,
          suggestedInterpretation: interpretation(line.suggestedInterpretation)
        }))
      }))
    }
  }
}

export const validateAttachment = (
  requisitionId: string,
  resultId: string,
  file: File | undefined,
  maxSize: number
): ToolErrors => {
  const errors: ToolErrors = {}
  if (!isIntegerId(requisitionId))
    errors['attachment-requisition'] =
      'Vedleggets rekvisisjons-ID må være et heltall.'
  if (!isIntegerId(resultId))
    errors['attachment-result'] =
      'Velg eller oppgi en gyldig resultat-ID (heltall).'
  if (!file) errors['attachment-file'] = 'Velg en PDF før opplasting.'
  else if (
    file.type
      ? file.type !== 'application/pdf'
      : !file.name.toLowerCase().endsWith('.pdf')
  )
    errors['attachment-file'] = 'Kun PDF-filer er tillatt.'
  else if (file.size > maxSize)
    errors['attachment-file'] =
      'Vedlegget er for stort. Maksimal størrelse er 20 MB.'
  return errors
}
